import Button from 'components/shared/Button';
import { PerformanceTest } from 'interfaces/performanceTest';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { getReport } from 'services/report';

interface Props {
  onCompare: (report: PerformanceTest) => any;
}

interface CompareRequestData {
  reportUuid: string;
}

const CompareReportForm: React.FC<Props> = ({ onCompare }) => {
  const router = useRouter();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [compareReport, setCompareReport] = useState<PerformanceTest>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const compareReportUuid = Array.isArray(router.query.compareReportUuid)
    ? router.query.compareReportUuid[0]
    : router.query.compareReportUuid;

  const handleLookup = useCallback(
    (data: CompareRequestData) => {
      setLoading(true);
      setError(null);

      getReport(data.reportUuid)
        .then((perfReport) => {
          setLoading(false);
          onCompare(perfReport);
          setCompareReport(perfReport);
          router.push(
            `${router.asPath.split('?')[0]}?compareReportUuid=${perfReport.id}`,
            undefined,
            {
              shallow: true,
            }
          );
        })
        .catch((err) => {
          setLoading(false);
          setError({
            message: `Failed to fetch report. ${err}`,
          });
          console.error(err);
        });
    },
    [onCompare, router]
  );

  useEffect(() => {
    if (compareReportUuid && !compareReport) {
      reset({ reportUuid: compareReportUuid });
      handleLookup({ reportUuid: compareReportUuid });
    }
  }, [reset, handleLookup, compareReportUuid, compareReport]);

  return (
    <form
      onSubmit={handleSubmit(handleLookup)}
      className="md:inline-flex my-2 md:flex-wrap"
    >
      <span className="w-full md:w-96 md:mr-5">
        <input
          id="reportUuid"
          className="block w-full mb-2 md:my-0 px-3 py-2 placeholder-gray-400 transition duration-150 ease-in-out border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
          type="text"
          name="listingId"
          placeholder="Report ID"
          autoComplete="off"
          {...register('reportUuid', {
            required: 'Please enter the report to compare with',
          })}
        />
        {(errors.reportUuid || error) && (
          <p className="mt-2 text-xs text-red-600">
            {errors.reportUuid?.message || error.message}
          </p>
        )}
      </span>
      <span className="w-full md:w-auto">
        <Button title="Compare" type="submit" isLoading={loading} />
      </span>
    </form>
  );
};

export default CompareReportForm;
